import React, { useEffect, useState } from 'react';
import { Form } from 'antd';
import { isEmpty } from 'lodash';
import { isMobile } from 'react-device-detect';
import classNames from 'classnames';

import './RequirementForm.less';
import BaseRequirementForm from '@/components/DynamicForm/BaseRequirementForm';
import {
  getFieldName,
  mergeRequirementFormData,
  unSortableList,
} from '@/components/DynamicForm/utils';
import { TCustomField, TEntryForm, TRequirementFieldConfig } from '@/components/DynamicForm/model';
import { FormInstance } from 'antd/es/form';
import type { DragEndEvent } from '@dnd-kit/core/dist/types';

export default function RequirementForm({
  form,
  requirement: originRequirement,
  forceUpdate = false,
  secretKey,
  ignoreFields,
  displayMode,
  onSortEnd,
  inModal,
  disabledFields,
  onFieldSelect,
  selectedItem,
  customFieldList,
  fields,
}: {
  form?: FormInstance;
  campId: string;
  requirement?: TEntryForm;
  forceUpdate?: boolean;
  secretKey?: string;
  ignoreFields?: string[];
  displayMode?: boolean;
  onSortEnd?(event: DragEndEvent): void;
  inModal?: boolean;
  disabledFields?: string[];
  onFieldSelect?: (fieldKey: string) => void;
  selectedItem?: any;
  customFieldList: TCustomField[];
  fields: TRequirementFieldConfig[];
}) {
  const [requirement, setRequirement] = useState<TEntryForm | undefined>(originRequirement);
  const formData = {};

  const isIgnoreField = (field: TRequirementFieldConfig) => {
    if (isEmpty(ignoreFields)) {
      return false;
    }
    const name = getFieldName(field);
    const { fieldType } = field;
    if (fieldType && ignoreFields?.includes(fieldType)) {
      return true;
    }
    if (name && ignoreFields?.includes(name)) {
      return true;
    }
    return false;
  };

  useEffect(() => {
    if (originRequirement) {
      const requirementType = originRequirement.requirementType || formData.requirementType;
      if (forceUpdate) {
        setRequirement(mergeRequirementFormData(originRequirement, formData, { requirementType }));
      } else {
        setRequirement((requirement) =>
          mergeRequirementFormData(requirement, formData, originRequirement, {
            requirementType: requirement.requirementType || requirementType,
          }),
        );
      }
    }
  }, [originRequirement, forceUpdate]);

  useEffect(() => {
    if (!isEmpty(formData)) {
      setRequirement((requirement) =>
        mergeRequirementFormData(requirement, formData, {
          requirementType: requirement.requirementType || formData.requirementType,
        }),
      );
    }
  }, [formData]);

  const renderForm = () => {
    return (
      <Form
        form={form}
        className={classNames('custom-requirement-form', {
          'custom-requirement-form-mobile': isMobile,
        })}
        size={isMobile ? 'large' : undefined}
        colon={true}
        layout="vertical"
      >
        <BaseRequirementForm
          isIgnoreField={isIgnoreField}
          requirement={requirement}
          customFieldList={customFieldList}
          fields={fields}
          secretKey={secretKey}
          disabledFields={disabledFields}
        />
      </Form>
    );
  };

  const renderSortableForm = () => {
    const sortableFieldList = fields.filter(({ fieldType }) => !unSortableList.includes(fieldType));
    const unSortableFieldList = fields.filter(({ fieldType }) =>
      unSortableList.includes(fieldType),
    );
    return (
      <Form
        className={classNames(
          'custom-requirement-form',
          { 'custom-requirement-form-mobile': isMobile },
          { 'display-mode': displayMode },
        )}
        size={isMobile ? 'large' : undefined}
        colon={true}
        layout="vertical"
      >
        <BaseRequirementForm
          requirement={requirement}
          customFieldList={customFieldList}
          fields={unSortableFieldList}
          selectedItem={selectedItem}
          onFieldSelect={onFieldSelect}
          disabledFields={disabledFields}
        />
        <BaseRequirementForm
          requirement={requirement}
          customFieldList={customFieldList}
          fields={sortableFieldList}
          sortable
          selectedItem={selectedItem}
          onFieldSelect={onFieldSelect}
          disabledFields={disabledFields}
          onSortEnd={onSortEnd}
        />
      </Form>
    );
  };

  return (
    <div className={classNames('custom-requirement-form-wrapper', { 'in-modal': inModal })}>
      {displayMode ? renderSortableForm() : renderForm()}
    </div>
  );
}
